// F-107 SEARCH-CLEAR-X: one shared "x" for every search / filter field. withClear(input, opts) adds a clear button INSIDE the field at its
// inline end (so it mirrors in RTL), shown only while the field has text. Nothing is wrapped: the button is positioned over the input
// from the input's own box, so every site keeps its layout and needs ONE line.
//   - a tap clears the value, dispatches a real `input` event (the field's own handler runs: results, saved state such as S._fileSearch),
//     calls opts.now() if given (debounced fields: an instant reset), then puts the focus back in the field;
//   - the input always reserves padding-inline-end for the x, so nothing shifts when it appears;
//   - the target is 40 x 40 px; pointerdown is cancelled so the field never loses focus (no keyboard flicker on a phone);
//   - input.syncClear() re-reads the value (for code that sets .value without an event, e.g. the storage picker's navigation).
// Exposed to index.html as window.HUD_SEARCH_CLEAR.withClear (HUD.withClear); modules import it directly.
const T = k => (window.HUD && window.HUD.T ? window.HUD.T(k) : k);
const SIZE = 40;
let _css = false;
function injectCss() {
  if (_css || document.getElementById('hud-clr-styles')) { _css = true; return; }
  const s = document.createElement('style'); s.id = 'hud-clr-styles';
  s.textContent = '.hud-clr{position:absolute;z-index:3;width:' + SIZE + 'px;height:' + SIZE + 'px;padding:0;border:0;background:transparent;color:#5a706b;'
    + 'display:none;align-items:center;justify-content:center;font:700 15px/1 system-ui,sans-serif;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation}'
    + '.hud-clr>span{width:22px;height:22px;border-radius:50%;background:#e8f0ee;display:flex;align-items:center;justify-content:center}'
    + '@media (hover:hover){.hud-clr:hover>span{background:#d4e6e1}}'
    + '.hud-clr:focus-visible{outline:2px solid #0d6b63;outline-offset:-4px;border-radius:8px}';
  document.head.appendChild(s); _css = true;
}

export function withClear(input, opts) {
  opts = opts || {};
  if (!input || input._hudClr) return input;
  injectCss();
  const btn = document.createElement('button');
  btn.type = 'button'; btn.className = 'hud-clr'; btn.setAttribute('aria-label', T('search_clear'));
  const glyph = document.createElement('span'); glyph.setAttribute('aria-hidden', 'true'); glyph.textContent = '✕'; btn.appendChild(glyph);
  input._hudClr = btn;

  // always reserve the room for the x (no shift when it appears); keep a larger padding the site already set
  const reserve = () => {
    const cs = getComputedStyle(input);
    if ((parseFloat(cs.paddingInlineEnd) || 0) < SIZE) input.style.paddingInlineEnd = SIZE + 'px';
  };
  const place = () => {
    const par = input.parentNode;
    if (!par || !input.isConnected) return;
    if (btn.parentNode !== par) par.appendChild(btn);
    const pcs = getComputedStyle(par);
    if (pcs.position === 'static') par.style.position = 'relative';
    // the side comes from the INPUT's own resolved direction, not the parent's: a dir="auto" field re-resolves as its text changes (Latin text
    // in a Hebrew page is LTR, and its padding-inline-end is on the right), and the button takes the same direction so inset-inline-end agrees.
    const rtl = getComputedStyle(input).direction === 'rtl';
    btn.style.direction = rtl ? 'rtl' : 'ltr';
    let edge, top;
    if (input.offsetParent === par) {
      // layout values (not transformed rects): a card that scales in (the search overlay) or a scrolled container still lines up
      if (!input.offsetWidth || !input.offsetHeight) return;
      edge = rtl ? input.offsetLeft : (par.clientWidth - input.offsetLeft - input.offsetWidth);
      top = input.offsetTop + (input.offsetHeight - SIZE) / 2;
    } else {
      const pr = par.getBoundingClientRect(), ir = input.getBoundingClientRect();
      if (!ir.width || !ir.height) return;
      edge = rtl ? (ir.left - pr.left - (parseFloat(pcs.borderLeftWidth) || 0)) : (pr.right - ir.right - (parseFloat(pcs.borderRightWidth) || 0));
      top = ir.top - pr.top - (parseFloat(pcs.borderTopWidth) || 0) + par.scrollTop + (ir.height - SIZE) / 2;
    }
    btn.style.insetInlineEnd = Math.round(edge + 2) + 'px';
    btn.style.top = Math.round(top) + 'px';
  };
  const sync = () => {
    reserve();
    const has = input.value !== '';
    btn.style.display = has ? 'flex' : 'none';
    if (has) place();
  };
  btn.addEventListener('pointerdown', e => e.preventDefault());   // the field keeps its focus
  btn.addEventListener('click', e => {
    e.preventDefault(); e.stopPropagation();
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));   // the field's own handler: results, saved state
    if (typeof opts.now === 'function') opts.now();                // debounced fields reset at once
    sync();
    try { input.focus(); } catch (_) {}
  });
  input.addEventListener('input', sync);
  input.syncClear = sync;
  try {
    if (typeof ResizeObserver === 'function') { const ro = new ResizeObserver(() => { if (input.isConnected && input.value !== '') place(); }); ro.observe(input); }
  } catch (_) {}
  sync();
  return input;
}

window.HUD_SEARCH_CLEAR = { withClear };
